import { useState, useEffect } from "react";
import { APP_CONFIG } from "../../appConfig";
import { sendResendEmail, buildRewardRequestEmail, buildRewardConfirmEmail } from "../../utils/email";
import { getCategories, fbGet, fbSet, DEMO_MODE } from "../../utils/firebase";

import iconSpelling from "../../assets/icons/icon-spelling.webp";
import iconMeanings from "../../assets/icons/icon-meanings.webp";
import iconSynonyms from "../../assets/icons/icon-synonyms.webp";
import iconAntonyms from "../../assets/icons/icon-antonyms.webp";

const HISTORY_ICON_MAP = {
  spelling: iconSpelling,
  meaning:  iconMeanings,
  synonym:  iconSynonyms,
  antonym:  iconAntonyms,
};

// ── Shared shelf card components ──────────────────────────────────────────────

function HeartBtn({ wishlisted, onClick }) {
  return (
    <button
      title={wishlisted ? "Remove from wishlist" : "Add to wishlist"}
      onClick={onClick}
      style={{
        background: wishlisted ? "rgba(236,72,153,.18)" : "rgba(255,255,255,.15)",
        border: "none", borderRadius: 7, cursor: "pointer",
        fontSize: ".85rem", padding: ".28rem .38rem", lineHeight: 1, color: wishlisted ? "#ec4899" : "inherit",
      }}
    >
      {wishlisted ? "♥" : "♡"}
    </button>
  );
}

export function ChildRewardsPage({ user, data, onDataChange, pointsEarned, settings }) {
  const [customName, setCustomName] = useState("");
  const [customCost, setCustomCost] = useState("");
  const [showCustom, setShowCustom] = useState(false);
  const [msg, setMsg]               = useState("");
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat]   = useState("all");
  const [wishlist, setWishlist]     = useState([]);

  useEffect(() => { getCategories().then(setCategories); }, []);

  useEffect(() => {
    if (DEMO_MODE) {
      try {
        const raw = localStorage.getItem(`wishlist_${user.id}`);
        if (raw) setWishlist(JSON.parse(raw));
      } catch {}
    } else {
      fbGet(`users/${user.id}/wishlist`).then(val => {
        if (Array.isArray(val)) setWishlist(val);
      });
    }
  }, [user.id]);

  async function toggleWishlist(rewardId) {
    const next = wishlist.includes(rewardId)
      ? wishlist.filter(id => id !== rewardId)
      : [...wishlist, rewardId];
    setWishlist(next);
    if (DEMO_MODE) {
      try { localStorage.setItem(`wishlist_${user.id}`, JSON.stringify(next)); } catch {}
    } else {
      await fbSet(`users/${user.id}/wishlist`, next);
    }
  }

  const myPoints   = data.points?.[user.id] || 0;
  const myRequests = (data.requests || []).filter(r => r.userId === user.id);
  const pendingIds = new Set(myRequests.filter(r => r.status === "pending").map(r => r.rewardId).filter(Boolean));
  const myGoalId   = data.goals?.[user.id] || null;

  function setGoal(rewardId) {
    const newGoals = { ...(data.goals || {}), [user.id]: rewardId === myGoalId ? null : rewardId };
    onDataChange({ ...data, goals: newGoals });
  }

  function fireRewardRequestEmails(reward, reqId, token) {
    const resend = settings?.resend || {};
    if (!resend.apiKey || !resend.fromEmail) return;
    const workerBase = settings?.rewardWorkerUrl || APP_CONFIG.rewardActionUrl || "";
    const parentTo   = [resend.parent1Email, resend.parent2Email].filter(Boolean).join(",");
    const childTo    = settings?.childEmails?.[user.id] || "";

    if (parentTo && workerBase) {
      const approveUrl = `${workerBase}?action=approve&reqId=${reqId}&childId=${user.id}&token=${token}`;
      const declineUrl = `${workerBase}?action=decline&reqId=${reqId}&childId=${user.id}&token=${token}`;
      sendResendEmail(resend.apiKey, resend.fromEmail, {
        to: parentTo, subject: `SpellWise Kids — ${user.name} requested a reward`,
        html: buildRewardRequestEmail({ child: user, reward, approveUrl, declineUrl }),
      });
    } else if (parentTo) {
      sendResendEmail(resend.apiKey, resend.fromEmail, {
        to: parentTo, subject: `SpellWise Kids — ${user.name} requested a reward`,
        html: buildRewardRequestEmail({ child: user, reward, approveUrl: "#", declineUrl: "#" }),
      });
    }
    if (childTo) {
      sendResendEmail(resend.apiKey, resend.fromEmail, {
        to: childTo, subject: `SpellWise Kids — Your reward request was sent! ⏳`,
        html: buildRewardConfirmEmail({ child: user, reward }),
      });
    }
  }

  function requestReward(reward) {
    if (myPoints < reward.cost) return;
    if (pendingIds.has(reward.id)) return;
    const token = Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
    const reqId = `req_${Date.now()}`;
    const req = { id: reqId, userId: user.id, userName: user.name, userAvatar: user.avatar, rewardId: reward.id, rewardName: reward.name, rewardEmoji: reward.emoji || "🎁", cost: reward.cost, status: "pending", date: Date.now(), custom: false, token };
    onDataChange({ ...data, requests: [...(data.requests || []), req] });
    fireRewardRequestEmails(reward, reqId, token);
    setMsg(`✅ Request sent for "${reward.name}"!`);
    setTimeout(() => setMsg(""), 3000);
  }

  function submitCustom() {
    if (!customName.trim() || !customCost || isNaN(parseInt(customCost))) return;
    const reward = { name: customName.trim(), emoji: "⭐", cost: parseInt(customCost), custom: true };
    const token  = Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
    const reqId  = `req_${Date.now()}`;
    const req    = { id: reqId, userId: user.id, userName: user.name, userAvatar: user.avatar, rewardId: null, rewardName: reward.name, rewardEmoji: reward.emoji, cost: reward.cost, status: "pending", date: Date.now(), custom: true, token };
    onDataChange({ ...data, requests: [...(data.requests || []), req] });
    fireRewardRequestEmails(reward, reqId, token);
    setCustomName(""); setCustomCost(""); setShowCustom(false);
    setMsg("✅ Custom request sent!");
    setTimeout(() => setMsg(""), 3000);
  }

  // ── History data ───────────────────────────────────────────────────────────
  const myHistory = [
    ...(data.sessions || []).filter(s => s.userId === user.id).map(s => {
      let label, iconKey;
      if      (s.type === "meaning")  { label = "Meaning Quiz";  iconKey = "meaning";  }
      else if (s.type === "synonym")  { label = "Synonyms Quiz"; iconKey = "synonym";  }
      else if (s.type === "antonym")  { label = "Antonyms Quiz"; iconKey = "antonym";  }
      else                            { label = "Spelling Test"; iconKey = "spelling"; }
      return { id: s.id, date: s.date, label, iconKey, delta: s.ptsEarned || 0, sign: 1 };
    }),
    ...(data.requests || []).filter(r => r.userId === user.id && r.status === "approved").map(r => ({
      id: r.id, date: r.date, label: r.rewardName, iconKey: "reward", delta: r.cost, sign: -1,
    })),
    ...(data.pointAdjustments || []).filter(a => a.userId === user.id).map(a => ({
      id: a.id, date: a.date,
      label: a.reason || (a.delta > 0 ? "Bonus points" : "Point deduction"),
      iconKey: a.delta > 0 ? "bonus" : "deduction",
      delta: Math.abs(a.delta), sign: a.delta > 0 ? 1 : -1,
    })),
  ].sort((a, b) => b.date - a.date);

  const withBalance = [];
  let running = 0;
  [...myHistory].reverse().forEach(h => { running += h.sign * h.delta; withBalance.unshift({ ...h, balance: running }); });

  // ── Reward bucketing ───────────────────────────────────────────────────────
  const allRewards = [...(data.rewards || [])].sort((a, b) => a.cost - b.cost);

  // Unfiltered tiers (hero uses these)
  const shelf1All = allRewards.filter(r => myPoints >= r.cost);

  // Category-filtered tiers
  const filtered = activeCat === "all" ? allRewards : allRewards.filter(r => r.categoryId === activeCat);
  const shelf1 = filtered.filter(r => myPoints >= r.cost);
  const shelf2 = filtered.filter(r => myPoints < r.cost && myPoints >= r.cost * 0.4);
  const shelf3 = filtered.filter(r => myPoints < r.cost * 0.4);

  // Hero = cheapest affordable reward (unfiltered)
  const heroReward = shelf1All[0] || null;

  // Progress bar target = cheapest unaffordable
  const nextReward = allRewards.find(r => myPoints < r.cost) || null;
  const pctToNext  = nextReward ? Math.min(99, Math.round((myPoints / nextReward.cost) * 100)) : 100;

  // Wishlist shelf
  const wishlistRewards = allRewards.filter(r => wishlist.includes(r.id));

  const PILL_ACTIVE = { background: "#2563eb", color: "#fff", border: "none" };
  const PILL_BASE   = { background: "var(--surface2)", color: "var(--ink)", border: "1.5px solid var(--border)" };

  return (
    <>
      <style>{`
        @keyframes bob { 0%,100%{transform:translateY(-50%)} 50%{transform:translateY(-65%)} }
        .rw-shelf::-webkit-scrollbar { display: none }
      `}</style>

      {msg && (
        <div style={{ background: "var(--green-light)", color: "var(--green)", padding: ".75rem 1rem", borderRadius: 12, fontWeight: 700, marginBottom: "1rem", fontSize: ".88rem" }}>
          {msg}
        </div>
      )}

      {/* ── 1. POINTS BALANCE BAR ─────────────────────────────────────────── */}
      <div className="card" style={{ marginBottom: "1.1rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          <div style={{ flexShrink: 0 }}>
            <div style={{ fontSize: "2.1rem", fontWeight: 900, lineHeight: 1, color: "var(--amber)" }}>🌟 {myPoints}</div>
            <div style={{ fontSize: ".68rem", color: "var(--muted)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".05em", marginTop: ".2rem" }}>Your Points</div>
            {pointsEarned > 0 && (
              <div style={{ fontSize: ".72rem", fontWeight: 800, color: "var(--green)", marginTop: ".2rem" }}>+{pointsEarned} today!</div>
            )}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            {nextReward ? (
              <>
                <div style={{ position: "relative", background: "var(--border)", borderRadius: 5, height: 10, overflow: "visible", marginBottom: ".45rem" }}>
                  <div style={{
                    height: "100%",
                    background: "linear-gradient(90deg, #f59e0b, #2563eb)",
                    borderRadius: 5,
                    width: `${pctToNext}%`,
                    transition: "width .4s",
                    position: "relative",
                    minWidth: myPoints > 0 ? 16 : 0,
                  }}>
                    {myPoints > 0 && (
                      <span style={{ position: "absolute", right: -9, top: "50%", fontSize: ".95rem", lineHeight: 1, animation: "bob 0.8s ease-in-out infinite" }}>🌟</span>
                    )}
                  </div>
                </div>
                <div style={{ fontSize: ".73rem", color: "var(--muted)", fontWeight: 600 }}>
                  <strong style={{ color: "var(--ink)" }}>{nextReward.cost - myPoints} pts</strong> to unlock {nextReward.emoji} {nextReward.name}
                </div>
              </>
            ) : (
              <div style={{ fontSize: ".88rem", fontWeight: 800, color: "var(--green)" }}>🎉 You can afford everything!</div>
            )}
          </div>
        </div>
      </div>

      {/* ── 2. HERO BANNER ────────────────────────────────────────────────── */}
      {heroReward && (
        <div style={{
          background: "linear-gradient(135deg, #1e3a8a, #2563eb)",
          borderRadius: 18, padding: "1.25rem 1.4rem", marginBottom: "1.25rem",
          display: "flex", alignItems: "center", gap: "1.1rem", color: "#fff",
        }}>
          <div style={{ fontSize: "3rem", lineHeight: 1, flexShrink: 0 }}>{heroReward.emoji}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "inline-block", background: "rgba(255,255,255,.2)", fontSize: ".68rem", fontWeight: 800, padding: ".2rem .65rem", borderRadius: 999, marginBottom: ".45rem", letterSpacing: ".04em" }}>
              ✨ You can afford this!
            </div>
            <div style={{ fontWeight: 900, fontSize: "1.05rem", lineHeight: 1.2 }}>{heroReward.name}</div>
            <div style={{ fontSize: ".78rem", opacity: .72, marginTop: ".2rem" }}>🌟 {heroReward.cost} pts</div>
          </div>
          <button
            onClick={() => requestReward(heroReward)}
            disabled={pendingIds.has(heroReward.id)}
            style={{
              background: "#fff", color: "#1e3a8a", border: "none", borderRadius: 12,
              padding: ".65rem 1rem", fontWeight: 800, fontSize: ".82rem",
              cursor: pendingIds.has(heroReward.id) ? "not-allowed" : "pointer",
              fontFamily: "'Nunito', sans-serif", flexShrink: 0,
              opacity: pendingIds.has(heroReward.id) ? .55 : 1,
            }}>
            {pendingIds.has(heroReward.id) ? "⏳ Sent" : "Claim Now →"}
          </button>
        </div>
      )}

      {/* ── 3. CATEGORY FILTER PILLS ──────────────────────────────────────── */}
      {allRewards.length > 0 && (
        <div style={{ display: "flex", gap: ".4rem", flexWrap: "wrap", marginBottom: "1.1rem" }}>
          <button
            onClick={() => setActiveCat("all")}
            style={{ ...activeCat === "all" ? PILL_ACTIVE : PILL_BASE, borderRadius: 999, padding: ".35rem .9rem", fontFamily: "'Nunito',sans-serif", fontWeight: 700, fontSize: ".8rem", cursor: "pointer" }}>
            All
          </button>
          {categories.map(c => (
            <button
              key={c.id}
              onClick={() => setActiveCat(c.id)}
              style={{ ...activeCat === c.id ? PILL_ACTIVE : PILL_BASE, borderRadius: 999, padding: ".35rem .9rem", fontFamily: "'Nunito',sans-serif", fontWeight: 700, fontSize: ".8rem", cursor: "pointer" }}>
              {c.emoji} {c.name}
            </button>
          ))}
        </div>
      )}

      {/* ── 4. THREE TIERED SHELVES ───────────────────────────────────────── */}

      {/* SHELF 1 — Ready to Claim */}
      {shelf1.length > 0 && (
        <div style={{ marginBottom: "1.25rem" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: ".65rem" }}>
            <div style={{ fontFamily: "'Fredoka One','Nunito',sans-serif", fontSize: "1.1rem", fontWeight: 700, color: "#16a34a" }}>🏆 Ready to Claim</div>
            <span style={{ background: "#16a34a", color: "#fff", fontSize: ".68rem", fontWeight: 800, padding: ".15rem .55rem", borderRadius: 999 }}>{shelf1.length}</span>
          </div>
          <div className="rw-shelf" style={{ display: "flex", gap: ".7rem", overflowX: "auto", paddingBottom: ".4rem", scrollbarWidth: "none" }}>
            {shelf1.map(r => {
              const isPending    = pendingIds.has(r.id);
              const isGoal       = myGoalId === r.id;
              const isWishlisted = wishlist.includes(r.id);
              return (
                <div key={r.id} style={{ minWidth: 130, flexShrink: 0, borderRadius: 14, background: "linear-gradient(135deg, #1e3a8a, #2563eb)", color: "#fff", display: "flex", flexDirection: "column", overflow: "hidden" }}>
                  <div style={{ height: 60, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "2.1rem", background: "rgba(0,0,0,.15)", position: "relative" }}>
                    {r.emoji}
                    <span style={{ position: "absolute", top: 5, right: 6 }}>
                      <HeartBtn wishlisted={isWishlisted} onClick={() => toggleWishlist(r.id)} />
                    </span>
                  </div>
                  <div style={{ padding: "10px 11px 12px", display: "flex", flexDirection: "column", gap: ".3rem", flex: 1 }}>
                    <div style={{ fontWeight: 800, fontSize: ".8rem", lineHeight: 1.25 }}>{r.name}</div>
                    <div style={{ fontSize: ".7rem", opacity: .75 }}>🌟 {r.cost} pts</div>
                    <div style={{ display: "flex", gap: ".3rem", marginTop: ".2rem" }}>
                      <button title={isGoal ? "Remove goal" : "Set as goal"} onClick={() => setGoal(r.id)}
                        style={{ background: isGoal ? "rgba(255,255,255,.35)" : "rgba(255,255,255,.15)", border: "none", borderRadius: 7, cursor: "pointer", fontSize: ".8rem", padding: ".28rem .38rem", lineHeight: 1 }}>🎯</button>
                      <button onClick={() => requestReward(r)} disabled={isPending}
                        style={{ flex: 1, padding: ".32rem .4rem", background: isPending ? "rgba(255,255,255,.2)" : "rgba(255,255,255,.95)", color: isPending ? "rgba(255,255,255,.6)" : "#1e3a8a", border: "none", borderRadius: 7, fontWeight: 800, fontSize: ".75rem", cursor: isPending ? "not-allowed" : "pointer", fontFamily: "'Nunito',sans-serif" }}>
                        {isPending ? "⏳ Sent" : "Claim →"}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* SHELF 2 — Getting Close */}
      {shelf2.length > 0 && (
        <div style={{ marginBottom: "1.25rem" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: ".65rem" }}>
            <div style={{ fontFamily: "'Fredoka One','Nunito',sans-serif", fontSize: "1.1rem", fontWeight: 700, color: "#f59e0b" }}>💪 Getting Close</div>
            <span style={{ background: "#f59e0b", color: "#fff", fontSize: ".68rem", fontWeight: 800, padding: ".15rem .55rem", borderRadius: 999 }}>{shelf2.length}</span>
          </div>
          <div className="rw-shelf" style={{ display: "flex", gap: ".7rem", overflowX: "auto", paddingBottom: ".4rem", scrollbarWidth: "none" }}>
            {shelf2.map(r => {
              const pct          = Math.round((myPoints / r.cost) * 100);
              const isGoal       = myGoalId === r.id;
              const isWishlisted = wishlist.includes(r.id);
              return (
                <div key={r.id} style={{ minWidth: 130, flexShrink: 0, borderRadius: 14, background: "var(--surface)", border: "1.5px solid #fde68a", display: "flex", flexDirection: "column", overflow: "hidden" }}>
                  <div style={{ height: 60, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "2.1rem", background: "#fef9c3", position: "relative" }}>
                    {r.emoji}
                    <span style={{ position: "absolute", top: 5, right: 6 }}>
                      <HeartBtn wishlisted={isWishlisted} onClick={() => toggleWishlist(r.id)} />
                    </span>
                  </div>
                  <div style={{ padding: "10px 11px 12px", display: "flex", flexDirection: "column", gap: ".3rem", flex: 1 }}>
                    <div style={{ fontWeight: 800, fontSize: ".8rem", lineHeight: 1.25, color: "var(--ink)" }}>{r.name}</div>
                    <div style={{ fontSize: ".7rem", color: "var(--amber)", fontWeight: 700 }}>🌟 {r.cost} pts</div>
                    <div style={{ background: "var(--border)", borderRadius: 3, height: 5, overflow: "hidden", marginTop: ".1rem" }}>
                      <div style={{ height: "100%", background: "linear-gradient(90deg, #f59e0b, #fbbf24)", borderRadius: 3, width: `${pct}%` }} />
                    </div>
                    <div style={{ fontSize: ".68rem", color: "var(--amber)", fontWeight: 700 }}>{pct}% there</div>
                    <button title={isGoal ? "Remove goal" : "Set as goal"} onClick={() => setGoal(r.id)}
                      style={{ marginTop: ".1rem", background: isGoal ? "var(--amber-light)" : "var(--surface2)", border: `1.5px solid ${isGoal ? "var(--amber)" : "var(--border)"}`, borderRadius: 7, cursor: "pointer", fontSize: ".72rem", fontWeight: 700, padding: ".28rem .5rem", color: isGoal ? "var(--amber)" : "var(--muted)", fontFamily: "'Nunito',sans-serif" }}>
                      {isGoal ? "🎯 Goal set" : "🎯 Set goal"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* SHELF 3 — Dream Big */}
      {shelf3.length > 0 && (
        <div style={{ marginBottom: "1.25rem" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: ".65rem" }}>
            <div style={{ fontFamily: "'Fredoka One','Nunito',sans-serif", fontSize: "1.1rem", fontWeight: 700, color: "#6b7280" }}>💫 Dream Big</div>
            <span style={{ background: "#6b7280", color: "#fff", fontSize: ".68rem", fontWeight: 800, padding: ".15rem .55rem", borderRadius: 999 }}>{shelf3.length}</span>
          </div>
          <div className="rw-shelf" style={{ display: "flex", gap: ".7rem", overflowX: "auto", paddingBottom: ".4rem", scrollbarWidth: "none" }}>
            {shelf3.map(r => {
              const pct          = Math.round((myPoints / r.cost) * 100);
              const isGoal       = myGoalId === r.id;
              const isWishlisted = wishlist.includes(r.id);
              return (
                <div key={r.id} style={{ minWidth: 130, flexShrink: 0, borderRadius: 14, background: "var(--surface)", border: "1px solid var(--border)", opacity: 0.7, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                  <div style={{ height: 60, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "2.1rem", background: "var(--surface2)", position: "relative" }}>
                    {r.emoji}
                    <span style={{ position: "absolute", top: 5, right: 6 }}>
                      <HeartBtn wishlisted={isWishlisted} onClick={() => toggleWishlist(r.id)} />
                    </span>
                  </div>
                  <div style={{ padding: "10px 11px 12px", display: "flex", flexDirection: "column", gap: ".3rem", flex: 1 }}>
                    <div style={{ fontWeight: 800, fontSize: ".8rem", lineHeight: 1.25, color: "var(--ink)" }}>{r.name}</div>
                    <div style={{ fontSize: ".7rem", color: "var(--muted)", fontWeight: 700 }}>🌟 {r.cost} pts</div>
                    <div style={{ background: "var(--border)", borderRadius: 3, height: 5, overflow: "hidden", marginTop: ".1rem" }}>
                      <div style={{ height: "100%", background: "#9ca3af", borderRadius: 3, width: `${Math.max(2, pct)}%` }} />
                    </div>
                    <div style={{ fontSize: ".68rem", color: "#6b7280", fontWeight: 700 }}>{pct}% there</div>
                    <button title={isGoal ? "Remove goal" : "Set as goal"} onClick={() => setGoal(r.id)}
                      style={{ marginTop: ".1rem", background: isGoal ? "#f3f4f6" : "var(--surface2)", border: `1.5px solid ${isGoal ? "#9ca3af" : "var(--border)"}`, borderRadius: 7, cursor: "pointer", fontSize: ".72rem", fontWeight: 700, padding: ".28rem .5rem", color: "#6b7280", fontFamily: "'Nunito',sans-serif" }}>
                      {isGoal ? "🎯 Goal set" : "🎯 Set goal"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* SHELF 4 — My Wishlist */}
      <div style={{ marginBottom: "1.25rem" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: ".65rem" }}>
          <div style={{ fontFamily: "'Fredoka One','Nunito',sans-serif", fontSize: "1.1rem", fontWeight: 700, color: "#ec4899" }}>♥ My Wishlist</div>
          {wishlistRewards.length > 0 && (
            <span style={{ background: "#ec4899", color: "#fff", fontSize: ".68rem", fontWeight: 800, padding: ".15rem .55rem", borderRadius: 999 }}>{wishlistRewards.length}</span>
          )}
        </div>
        {wishlistRewards.length === 0 ? (
          <div style={{ color: "var(--muted)", fontSize: ".85rem", fontWeight: 600, padding: ".5rem 0" }}>
            Tap ♡ on any reward to save it here
          </div>
        ) : (
          <div className="rw-shelf" style={{ display: "flex", gap: ".7rem", overflowX: "auto", paddingBottom: ".4rem", scrollbarWidth: "none" }}>
            {wishlistRewards.map(r => {
              const isPending = pendingIds.has(r.id);
              const isGoal    = myGoalId === r.id;
              const pct       = Math.round((myPoints / r.cost) * 100);
              const canClaim  = myPoints >= r.cost;
              return (
                <div key={r.id} style={{ minWidth: 130, flexShrink: 0, borderRadius: 14, background: "var(--surface)", border: "2px solid #f9a8d4", display: "flex", flexDirection: "column", overflow: "hidden" }}>
                  <div style={{ height: 60, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "2.1rem", background: "#fdf2f8", position: "relative" }}>
                    {r.emoji}
                    <span style={{ position: "absolute", top: 5, right: 6 }}>
                      <HeartBtn wishlisted={true} onClick={() => toggleWishlist(r.id)} />
                    </span>
                  </div>
                  <div style={{ padding: "10px 11px 12px", display: "flex", flexDirection: "column", gap: ".3rem", flex: 1 }}>
                    <div style={{ fontWeight: 800, fontSize: ".8rem", lineHeight: 1.25, color: "var(--ink)" }}>{r.name}</div>
                    <div style={{ fontSize: ".7rem", color: "#ec4899", fontWeight: 700 }}>🌟 {r.cost} pts</div>
                    {!canClaim && (
                      <>
                        <div style={{ background: "var(--border)", borderRadius: 3, height: 5, overflow: "hidden", marginTop: ".1rem" }}>
                          <div style={{ height: "100%", background: "linear-gradient(90deg,#f9a8d4,#ec4899)", borderRadius: 3, width: `${Math.max(2, pct)}%` }} />
                        </div>
                        <div style={{ fontSize: ".68rem", color: "#ec4899", fontWeight: 700 }}>{pct}% there</div>
                      </>
                    )}
                    <div style={{ display: "flex", gap: ".3rem", marginTop: ".2rem" }}>
                      <button title={isGoal ? "Remove goal" : "Set as goal"} onClick={() => setGoal(r.id)}
                        style={{ background: isGoal ? "#fce7f3" : "var(--surface2)", border: `1.5px solid ${isGoal ? "#f9a8d4" : "var(--border)"}`, borderRadius: 7, cursor: "pointer", fontSize: ".8rem", padding: ".28rem .38rem", lineHeight: 1 }}>🎯</button>
                      {canClaim && (
                        <button onClick={() => requestReward(r)} disabled={isPending}
                          style={{ flex: 1, padding: ".32rem .4rem", background: isPending ? "#fce7f3" : "#ec4899", color: isPending ? "#ec4899" : "#fff", border: "none", borderRadius: 7, fontWeight: 800, fontSize: ".75rem", cursor: isPending ? "not-allowed" : "pointer", fontFamily: "'Nunito',sans-serif" }}>
                          {isPending ? "⏳ Sent" : "Claim →"}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {allRewards.length === 0 && (
        <div className="empty-state">No rewards yet — ask your parent to add some!</div>
      )}

      {/* ── Custom request ────────────────────────────────────────────────── */}
      <div className="card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div className="card-title">💡 Request Something Else</div>
            <div className="card-sub">Suggest your own reward</div>
          </div>
          <button className="wm-btn secondary" onClick={() => setShowCustom(s => !s)}>{showCustom ? "Cancel" : "+ Request"}</button>
        </div>
        {showCustom && (
          <div className="custom-req-form" style={{ marginTop: "1rem" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: ".65rem" }}>
              <input className="wm-input" placeholder="What would you like? e.g. Trip to the movies" value={customName} onChange={e => setCustomName(e.target.value)} />
              <input className="wm-input" placeholder="Points you think it's worth" type="number" min="1" value={customCost} onChange={e => setCustomCost(e.target.value)} style={{ maxWidth: 200 }} />
              <button className="wm-btn" onClick={submitCustom} disabled={!customName.trim() || !customCost}>Send Request</button>
            </div>
          </div>
        )}
      </div>

      {/* ── 5. POINTS HISTORY ─────────────────────────────────────────────── */}
      <div className="card">
        <div className="card-title">📊 Points History</div>
        <div className="card-sub" style={{ marginBottom: ".9rem" }}>Your earned and spent points</div>
        {withBalance.length === 0
          ? <div style={{ color: "var(--muted)", fontSize: ".88rem" }}>No activity yet — complete a test to earn points!</div>
          : withBalance.map(h => (
            <div key={h.id} style={{ display: "flex", alignItems: "center", gap: ".65rem", padding: ".7rem 0", borderBottom: "1px solid var(--border)" }}>
              {HISTORY_ICON_MAP[h.iconKey]
                ? <img src={HISTORY_ICON_MAP[h.iconKey]} alt="" style={{ width: 26, height: 26, borderRadius: 6, objectFit: "cover", flexShrink: 0 }} />
                : <span style={{ fontSize: "1.1rem", width: 26, textAlign: "center", flexShrink: 0 }}>🎁</span>
              }
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: ".88rem" }}>{h.label}</div>
                <div style={{ fontSize: ".73rem", color: "var(--muted)", marginTop: ".1rem" }}>{new Date(h.date).toLocaleDateString("en-AU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</div>
              </div>
              <div style={{ textAlign: "right", flexShrink: 0 }}>
                <div style={{ fontWeight: 800, fontSize: ".95rem", color: h.sign === 1 ? "var(--green)" : "var(--red)" }}>
                  {h.sign === 1 ? "+" : "−"}{h.delta}
                </div>
                <div style={{ fontSize: ".72rem", color: "var(--muted)", fontWeight: 600 }}>bal: {h.balance}</div>
              </div>
            </div>
          ))}
      </div>

      {/* ── My Requests ───────────────────────────────────────────────────── */}
      {myRequests.length > 0 && (
        <div className="card">
          <div className="card-title">📋 My Requests</div>
          <div className="card-sub" style={{ marginBottom: ".9rem" }}>Your recent reward requests</div>
          {myRequests.slice().reverse().map(r => (
            <div key={r.id}
              className={`req-card ${r.status === "declined" ? "" : r.status}`}
              style={r.status === "declined" ? { border: "1.5px solid #0891b2", borderRadius: 14, padding: "1rem 1.25rem", marginBottom: ".6rem" } : {}}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div>
                  <span style={{ fontWeight: 700 }}>{r.rewardEmoji} {r.rewardName}</span>
                  {r.status === "declined"
                    ? <span style={{ display: "inline-block", marginLeft: ".5rem", fontSize: ".72rem", fontWeight: 700, padding: ".18rem .55rem", borderRadius: 999, background: "#cffafe", color: "#0891b2" }}>↩ Returned to Bank</span>
                    : <span className={`req-status ${r.status}`}>{r.status === "pending" ? "⏳ Pending" : "✅ Approved"}</span>
                  }
                  {r.custom && <span style={{ fontSize: ".72rem", color: "var(--muted)", marginLeft: ".4rem" }}>(custom)</span>}
                </div>
                <div style={{ fontSize: ".82rem", color: "var(--muted)", fontWeight: 700 }}>⭐ {r.cost}</div>
              </div>
              {r.parentNote && <div style={{ fontSize: ".8rem", color: "var(--muted)", marginTop: ".35rem", fontStyle: "italic" }}>"{r.parentNote}"</div>}
              <div style={{ fontSize: ".72rem", color: "var(--muted)", marginTop: ".25rem" }}>{new Date(r.date).toLocaleDateString("en-AU", { day: "numeric", month: "short" })}</div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
